import { useState } from 'react'
import { Link } from '@overdose/components'
import { IconX } from '@tabler/icons-react'
import classNames from 'classnames'
import { Button } from '~/components/Button'
import { Tooltip } from '~/components/Tooltip'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { formatCustomerAddress } from '~/helpers/formatCustomerAddress'
import styles from './StoreMapItemTooltip.module.css'
import {
  StoreMapItemInfowindowProps,
  StoreMapItemTooltipProps,
} from './StoreMapItemTooltip.types'

export const StoreMapItemInfoWindow = ({
  store,
  onClose,
}: StoreMapItemInfowindowProps) => {
  const {
    displayName = '',
    name,
    address,
    url,
    phoneNumber,
    googlePlaceId,
  } = store

  const destination = encodeURIComponent(formatCustomerAddress(address))
  const directionUrl = `https://www.google.com/maps/search/?api=1&query=${destination}&query_place_id=${googlePlaceId}`
  const storeName = displayName || name
  const isReseller = storeName.toLowerCase().includes('reseller')

  return (
    <div
      className={classNames(
        'p-4 text-secondary-muted rounded-md overflow-hidden',
        styles.mapInfoContainer
      )}>
      <div className='flex items-start gap-x-2'>
        <div className='text-primary-heading flex-1'>
          <Typography
            className={classNames({
              [styles.resellerTextColor]: isReseller,
            })}
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyRegularBold}>
            {storeName}
          </Typography>
        </div>
        <Button
          size='xs'
          variant='ghost'
          theme={{ root: styles.close }}
          icon={<IconX />}
          iconOnly
          onClick={() => {
            return onClose && onClose()
          }}
        />
      </div>
      <div className='mt-1'>
        <Typography tag={TypographyTag.p} variant={TypographyVariant.BodySmall}>
          {address.streetNumber} {address.streetName}, {address.city},<br />
          {address.state} {address.postalCode}, {address.country}
        </Typography>
      </div>
      <div className='mt-1'>
        <Typography tag={TypographyTag.p} variant={TypographyVariant.BodySmall}>
          <span className='block leading-6'>{phoneNumber}</span>
        </Typography>
      </div>
      <div className='flex items-center gap-x-4 mt-3'>
        {url && (
          <Link to={url}>
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodySmallBold}>
              View Details
            </Typography>
          </Link>
        )}
        <Link to={directionUrl} target='_blank'>
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodySmallBold}>
            Get Directions
          </Typography>
        </Link>
      </div>
    </div>
  )
}

export const StoreMapItemTooltip = ({
  store,
  children,
}: StoreMapItemTooltipProps) => {
  const [visible, setVisible] = useState(false)

  return (
    <Tooltip
      theme={{ root: styles.root }}
      content={
        <StoreMapItemInfoWindow
          store={store}
          onClose={() => {
            return setVisible(false)
          }}
        />
      }
      trigger='click'
      popupVisible={visible}
      triggerProps={{
        onOpenDelay: 100,
        onOpen: () => {
          return setVisible(true)
        },
      }}
      position='top'
      offset={[0, 0]}>
      {children}
    </Tooltip>
  )
}
